import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";
import { FileText } from "lucide-react";

import messages from "../../../assets/locale/messages";
import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import {
  FilePreviewModal,
  TabSetsLevels,
  UIEmptyState,
  UIPaginationBar,
} from "../../../components/ui";
import PublishingRequestsTable from "../../../components/PublishingRequests/PublishingRequestsTable";
import AcceptRequestModal from "../../../components/PublishingRequests/modals/AcceptRequestModal";
import RejectRequestModal from "../../../components/PublishingRequests/modals/RejectRequestModal";
import RequestAcceptedModal from "../../../components/PublishingRequests/modals/RequestAcceptedModal";
import RequestCancelledModal from "../../../components/PublishingRequests/modals/RequestCancelledModal";
import {
  EMPTY_REQUEST_FILTERS,
  REQUEST_STATUSES,
  REQUEST_TABS,
  REQUEST_TYPES,
  buildRequestsParams,
  fillTemplate,
  hasActiveFilters,
  mapPaymentRequest,
} from "../../../components/PublishingRequests/publishingRequestsUtils";
import useDebounce from "../../../hooks/useDebounce";
import { ITEMS_PER_PAGE } from "../../../network/apis/companies";
import {
  acceptPaymentRequest,
  getPaymentRequest,
  getPaymentRequests,
  rejectPaymentRequest,
} from "../../../network/apis/paymentRequests";
import { getApprovedCompaniesRequest } from "../../../store/Lookups/actions";
import { getPortalApiErrorMessage } from "../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../utils/Helpers";
import { isSuperAdmin } from "../../../utils/Permissions";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";

import "../../../components/PublishingRequests/PublishingRequests.scss";

const SEARCH_DEBOUNCE_MS = 450;

const getTotalCount = (response) =>
  Number(response?.headers?.["total-count"] || 0);

/** Decisions show their own error toast, not also the interceptor's. */
const QUIET = { handlerEnabled: false };

const isNetworkError = (error) => error?.message === "Network Error" && !error?.response;

const PublishingRequests = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const companies = useSelector((state) => state.lookups.approvedCompanies);
  const { publishingRequests: t, shared, clientReports } = messages[lang];
  const superAdmin = isSuperAdmin();

  /** Requests of the open page; null until the first answer. */
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  /** Bumped to load the list and the counter again after a decision. */
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState(REQUEST_TABS.pending);
  const [filters, setFilters] = useState(EMPTY_REQUEST_FILTERS);
  /** Bumped to reset the uncontrolled parts of the filter bar (date input). */
  const [filtersKey, setFiltersKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [acceptTarget, setAcceptTarget] = useState(null);
  /** The accept is being sent: its button waits, so a double click sends one request. */
  const [accepting, setAccepting] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [accepted, setAccepted] = useState(null);
  const [proof, setProof] = useState(null);
  const [cancelledOpen, setCancelledOpen] = useState(false);
  /** The request a notification opened the page for (`?request_id=`); its row is highlighted. */
  // `key` changes on every notification click, even one for the page already open.
  const { search: locationSearch, key: locationKey } = useLocation();
  const notifiedId = new URLSearchParams(locationSearch).get("request_id");
  const [highlightedId, setHighlightedId] = useState(null);

  const typedSearch = filters.search.trim();
  const debouncedSearch = useDebounce(typedSearch, SEARCH_DEBOUNCE_MS);
  // A cleared search (clear action, tab change) applies at once.
  const search = typedSearch ? debouncedSearch : "";

  // `lang` — company names come back in the request locale.
  useEffect(() => {
    if (superAdmin) dispatch(getApprovedCompaniesRequest());
  }, [dispatch, lang, superAdmin]);

  useEffect(() => {
    if (!superAdmin) return undefined;
    let ignore = false;
    getPaymentRequests(
      buildRequestsParams({ tab, filters, search, page, pageSize }),
    )
      .then((response) => {
        if (ignore) return;
        const list = (response.data?.data ?? []).map(mapPaymentRequest);
        const count = getTotalCount(response);
        // The last row of the last page was decided: step back to the new last page.
        if (!list.length && count && page > 1) {
          setPage(Math.ceil(count / pageSize));
          return;
        }
        setRows(list);
        setTotal(count);
        // The Pending counter shows what remains after the search and the filters (PROLINE-4183).
        if (tab === REQUEST_TABS.pending) setPendingCount(count);
      })
      .catch(() => {
        if (!ignore) setRows((current) => current ?? []);
      });
    return () => {
      ignore = true;
    };
    // `filters.search` is applied through the debounced `search`.
  }, [
    superAdmin,
    tab,
    search,
    filters.company,
    filters.type,
    filters.date,
    filters.status,
    page,
    pageSize,
    reloadKey,
  ]);

  // From a notification: open the tab holding the request (pending, or already handled),
  // and reload the list and the counter so the new request shows.
  useEffect(() => {
    if (!superAdmin || !notifiedId) return undefined;
    let ignore = false;
    setReloadKey((current) => current + 1);
    getPaymentRequest(notifiedId)
      .then((response) => {
        if (ignore) return;
        const status = response.data?.data?.status;
        setTab(
          status === REQUEST_STATUSES.pending
            ? REQUEST_TABS.pending
            : REQUEST_TABS.done,
        );
        setFilters(EMPTY_REQUEST_FILTERS);
        setPage(1);
        setHighlightedId(notifiedId);
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, [superAdmin, notifiedId, locationKey]);

  // Once its row is on screen: scroll down (or up) to it, then let the highlight fade.
  // The data grid renders its rows after the list arrives, so the row is looked up
  // for a moment before giving up.
  useEffect(() => {
    if (!highlightedId || !rows?.length) return undefined;
    let tries = 0;
    let fadeTimer;
    const findTimer = window.setInterval(() => {
      const row = document.querySelector(`[data-id="${highlightedId}"]`);
      tries += 1;
      if (!row && tries < 20) return;
      window.clearInterval(findTimer);
      row?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      fadeTimer = window.setTimeout(() => setHighlightedId(null), 4000);
    }, 100);
    return () => {
      window.clearInterval(findTimer);
      window.clearTimeout(fadeTimer);
    };
  }, [highlightedId, rows]);

  // On the Done tab, whose search and filters don't apply to pending requests, the counter
  // keeps all of them. On the Pending tab it comes from the list itself (above).
  useEffect(() => {
    if (!superAdmin || tab === REQUEST_TABS.pending) return;
    getPaymentRequests({ status: REQUEST_STATUSES.pending, items: 1 })
      .then((response) => setPendingCount(getTotalCount(response)))
      .catch(() => {});
  }, [superAdmin, reloadKey, tab]);

  if (!superAdmin) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const updateFilters = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_REQUEST_FILTERS);
    setFiltersKey((key) => key + 1);
    setPage(1);
  };

  const reload = () => setReloadKey((key) => key + 1);

  /** Shows the reason the backend gives (e.g. the company cancelled the request), then refreshes. */
  const handleError = (error) => {
    showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
    reload();
  };

  /** The popups show the crew size, which only the detailed request carries. */
  const openDecision = async (row, setTarget) => {
    try {
      const response = await getPaymentRequest(row.id);
      setTarget(mapPaymentRequest(response.data.data));
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * A failed decision. Offline, nothing was decided: the popup stays open to try again
   * (returns true). When the company cancelled the request while its popup was open, the
   * popup turns into "Request Cancelled"; any other failure shows the backend's reason.
   */
  const handleDecisionError = async (request, error) => {
    if (isNetworkError(error)) {
      showErrorMsg(shared.noInternetConnection);
      return true;
    }
    try {
      const response = await getPaymentRequest(request.id, QUIET);
      if (response.data?.data?.status === REQUEST_STATUSES.cancelled) {
        setCancelledOpen(true);
        return false;
      }
    } catch {
      // Fall back to the original error below.
    }
    handleError(error);
    return false;
  };

  const accept = async (request) => {
    setAccepting(true);
    try {
      await acceptPaymentRequest(request.id, QUIET);
      setAcceptTarget(null);
      setAccepted(request);
      reload();
    } catch (error) {
      if (!(await handleDecisionError(request, error))) setAcceptTarget(null);
    } finally {
      setAccepting(false);
    }
  };

  const reject = async (request, reason) => {
    try {
      const response = await rejectPaymentRequest(request.id, reason, QUIET);
      // The backend's message, or the story's while the API returns none.
      const message = response?.data?.message || t[request.type].rejectedToast;
      if (message) showSuccessMsg(message, true);
      reload();
    } catch (error) {
      // Thrown again so the reject popup stays open when nothing was decided.
      if (await handleDecisionError(request, error)) throw error;
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pages);
  const isPendingTab = tab === REQUEST_TABS.pending;
  const filtered = hasActiveFilters(filters);

  const renderBody = () => {
    if (!rows) return null;
    if (!rows.length) {
      return (
        <div className="publishing-requests-page__empty">
          <UIEmptyState
            icon={<FileText aria-hidden />}
            classNames={{ root: "ui-empty-state--embedded" }}
            {...(filtered
              ? {
                  title: t.emptyResultsTitle,
                  description: t.emptyResultsDescription,
                  actionLabel: t.clearFilters,
                  onAction: clearFilters,
                }
              : {
                  title: isPendingTab ? t.emptyPendingTitle : t.emptyDoneTitle,
                  description: isPendingTab
                    ? t.emptyPendingDescription
                    : t.emptyDoneDescription,
                })}
          />
        </div>
      );
    }

    return (
      <>
        <PublishingRequestsTable
          highlightedId={highlightedId}
          rows={rows}
          tab={tab}
          onAccept={(row) => openDecision(row, setAcceptTarget)}
          onReject={(row) => openDecision(row, setRejectTarget)}
          onPreviewProof={setProof}
        />
        <UIPaginationBar
          currentPage={currentPage}
          pages={pages}
          pageSize={pageSize}
          rangeTemplate={t.showingRange}
          rangeFrom={(currentPage - 1) * pageSize + 1}
          rangeTo={Math.min(currentPage * pageSize, total)}
          rangeTotal={total}
          rowsPerPageLabel={shared.numberOfRows}
          onPageChange={setPage}
          onPageSizeChange={(nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          }}
        />
      </>
    );
  };

  return (
    <div className="publishing-requests-page">
      <div className="mt-3">
        <Breadcrumbs
          list={[
            { path: "", label: t.requestsBreadcrumb },
            { path: "", label: t.listBreadcrumb },
          ]}
          BreadCrumbHasBackIcon={false}
        />
      </div>

      <TabSetsLevels
        value={tab}
        // The search and filters belong to one tab only.
        onChange={(nextTab) => {
          setTab(nextTab);
          clearFilters();
        }}
        tabs={[
          {
            value: REQUEST_TABS.pending,
            label: fillTemplate(t.pendingTabWithCount, {
              count: pendingCount.toLocaleString("en-US"),
            }),
          },
          { value: REQUEST_TABS.done, label: t.doneTab },
        ]}
      />

      {/* Figma: the search and "Filter" stay on an empty tab too. */}
      <Filters
        key={filtersKey}
        isCollapsable
        searchInput={{
          placeholder: t.searchPlaceholder,
          name: "publishingRequestsSearch",
          value: filters.search,
          onChange: (search) => updateFilters({ search }),
        }}
        filters={[
          {
            type: "filter",
            name: "company",
            placeholder: t.filterCompany,
            value: filters.company,
            options: companies,
            onChange: (company) => updateFilters({ company }),
          },
          {
            type: "filter",
            name: "requestType",
            placeholder: t.filterType,
            value: filters.type,
            options: Object.values(REQUEST_TYPES).map((type) => ({
              id: type,
              name: t.types[type],
            })),
            onChange: (type) => updateFilters({ type }),
          },
          {
            type: "date",
            name: "requestDate",
            placeholder: t.filterDate,
            value: filters.date,
            onChange: (date) => updateFilters({ date }),
          },
          // Every Pending row has the same status, so the status filter is for the other tab only.
          ...(isPendingTab
            ? []
            : [
                {
                  type: "filter",
                  name: "requestStatus",
                  placeholder: t.filterStatus,
                  value: filters.status,
                  options: Object.keys(t.statuses).map((status) => ({
                    id: status,
                    name: t.statuses[status],
                  })),
                  onChange: (status) => updateFilters({ status }),
                },
              ]),
        ]}
      />

      <div className="d-flex flex-column justify-content-between">
        {renderBody()}
      </div>

      <AcceptRequestModal
        request={acceptTarget}
        submitting={accepting}
        onClose={() => setAcceptTarget(null)}
        onConfirm={accept}
      />
      <RejectRequestModal
        request={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={reject}
      />
      <RequestAcceptedModal request={accepted} onClose={() => setAccepted(null)} />
      {/* Close refreshes the Pending tab, where the cancelled request is no longer listed. */}
      <RequestCancelledModal
        open={cancelledOpen}
        onClose={() => {
          setCancelledOpen(false);
          reload();
        }}
      />

      {/* The proof opens in a preview (PROLINE-4181); Download opens the file, as before. */}
      <FilePreviewModal
        open={Boolean(proof)}
        fileName={proof?.name}
        fileUrl={proof?.url}
        isRtl={lang === "ar"}
        labels={{
          download: shared.download,
          close: shared.close,
          unsupported: clientReports.previewModal.unsupportedType,
          loadError: clientReports.previewModal.loadError,
          pageOf: clientReports.previewModal.pageOf,
          previousPage: clientReports.previewModal.previousPage,
          nextPage: clientReports.previewModal.nextPage,
        }}
        onDownload={() => window.open(proof?.url, "_blank", "noopener,noreferrer")}
        onClose={() => setProof(null)}
      />
    </div>
  );
};

export default PublishingRequests;
